<template>
  <div
    class="
      tw-bg-sf-snow-lighter tw-h-full tw-overflow-hidden tw-flex tw-flex-col
    "
  >
    <div
      class="
        tw-shadow-sidepane tw-sticky tw-top-0 tw-bg-sf-snow-lighter tw-z-10
      "
    >
      <div :class="baseTitleRowClasses">
        <h3
          class="tw-text-lg tw-flex tw-items-center"
          :class="baseTitleClasses"
        >
          {{ I18n.t('sidebar_insights_properties.title') }}
          <span
            v-tooltip="I18n.t('sidebar_insights_properties.info_tooltip')"
            data-tooltip-size="sm"
            class="
              sf-icon
              fa fa-info-circle
              tw-text-sf-neutral-light-2 tw-text-xs tw-ml-2
            "
          ></span>
        </h3>

        <div class="tw-ml-auto">
          <SfButtonDs
            variant="tertiary"
            icon-only
            icon-before="arrow-right"
            :aria-label="I18n.t('common.close')"
            data-testid="drawer-close"
            @click="drawerStore.closeRightDrawer"
          />
        </div>
      </div>
    </div>

    <div
      class="tw-flex tw-flex-col tw-gap-4 tw-p-6 tw-flex-1 tw-overflow-hidden"
    >
      <SfSearch
        v-if="showSearch"
        v-model="searchValue"
        :show-search-icon="true"
        :show-clear-icon="true"
        :show-result-navigation="false"
        :bottom-margin="true"
        :focused-at="openedAt"
        :placeholder="I18n.t('sidebar_insights_properties.search_placeholder')"
        @clear="onClear"
      />
      <div class="tw-flex-1 tw-relative">
        <SfLoadingDots v-if="loading" class="tw-justify-center tw-mt-12" />

        <div v-else-if="emptyState" class="tw-px-16 tw-text-center">
          <img
            :src="$imagesRequire('insights/empty-state.svg')"
            class="tw-mb-3"
          />
          {{ emptyStateMessage }}
        </div>

        <div v-else class="tw-inset-0 tw-absolute tw-overflow-y-auto">
          <SfInpageList>
            <SidebarInsightsProperty
              v-for="item in filteredItems"
              :key="item.name"
              :item="item"
              :original-reconciliation-text-id="
                props.extraData.original_reconciliation_text_id
              "
              :period-date="props.extraData.period_date"
            />
          </SfInpageList>
        </div>
      </div>

      <div class="tw-bg-neutral-100 tw-p-3 tw-rounded-lg">
        {{ I18n.t('sidebar_insights_properties.support_message') }}
        <a
          data-testid="insights-properties-support-link"
          @click.prevent="openSupportMenu"
        >
          {{ I18n.t('sidebar_insights_properties.support_link_text') }}
        </a>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useContext } from '@app/shared/pinia/context'
import type { SilverfinContext } from '@app/shared/types/sf-data'
import { storeToRefs } from 'pinia'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { getInsightsProperties } from './api'
import type { InsightsProperty } from './types'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import SfLoadingDots from '@app/shared/vue/components/loaders/sf-loading-dots.vue'
import SidebarInsightsProperty from './sidebar-insights-property.vue'
import { openSupportMenu } from '@app/adapters/support-menu'
import { SfLog } from '@app/adapters/logger'

interface Props {
  extraData: {
    original_reconciliation_text_id: number
    period_date: string
  }
}

const props = defineProps<Props>()

const drawerStore = useDrawer()
const I18n = useI18n()
const openedAt = Date.now()

const items = ref<InsightsProperty[]>([])
const baseTitleRowClasses = [
  'tw-flex',
  'tw-items-center',
  'tw-px-6',
  'tw-py-3',
  'tw-border-solid',
  'tw-border-b',
  'tw-border-x-0',
  'tw-border-t-0',
  'tw-border-sf-neutral-lighter-2',
  'tw-h-[var(--secondary-subheader-height)]',
]
const baseTitleClasses = ['tw-my-0', 'tw-text-sf-dark-dark']

const contextStore = useContext()
const { context } = storeToRefs(contextStore)

const { searchValue, results: filteredItems } = useSearchable<
  InsightsProperty,
  InsightsProperty
>(items, {
  prepareItem: (item) => item,
  fuzzySortOptions: { keys: ['name', 'value'] },
})

const emptyState = computed(() => filteredItems.value.length === 0)
const showSearch = computed(() => items.value.length > 0)
const emptyStateMessage = computed(() =>
  searchValue.value.length > 0
    ? I18n.t('sidebar_insights_properties.empty_state_with_search')
    : I18n.t('sidebar_insights_properties.empty_state')
)

const loading = ref(true)

onMounted(async () => {
  document.body.click() // Close any open Bootstrap dropdowns

  const contextType = context.value.type as
    | SilverfinContext.Account
    | SilverfinContext.ReconciliationText

  try {
    const insightsProperties = await getInsightsProperties({
      contextType,
      contextId: context.value.id,
    })

    items.value = insightsProperties || []
  } catch (error) {
    SfLog.error('Error fetching insights properties:', {
      error,
      extra: {
        contextType,
        contextId: context.value.id,
      },
    })
  } finally {
    loading.value = false
  }
})

function onClear(): void {
  searchValue.value = ''
}
</script>
